/**
 * CP-14.6.3 — RFQ inbox + detail pages.
 *
 * Two top-level pages mount at /app/rfq (inbox with Sent / Inbox tabs) and
 * /app/rfq/:id (detail with snapshot card, status, accept/decline/withdraw,
 * text-only thread, and audit drawer).
 *
 * Operator rules honored:
 *   - Frontend never mutates the snapshot. The detail page renders snapshot
 *     fields as read-only.
 *   - Messaging is text-only (no upload / image picker / rich text).
 *   - Composer is disabled when rfq.status is withdrawn or expired (per
 *     CP-14.6.2 backend rule §11).
 *   - Accept / Decline visible only to the recipient side (and admin).
 *   - Withdraw visible only to the sender side.
 *   - No counter-offer, no rate edit, no booking trigger anywhere.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";

const STATUS_TONE = {
  sent: "bg-sky-50 text-sky-700 border-sky-200",
  accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-rose-50 text-rose-700 border-rose-200",
  withdrawn: "bg-slate-100 text-slate-600 border-slate-200",
  expired: "bg-amber-50 text-amber-700 border-amber-200",
};

function StatusBadge({ status, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        STATUS_TONE[status] || STATUS_TONE.sent
      }`}
    >
      {status}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function expiryLabel(iso, status) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = d.getTime() - Date.now();
  if (status !== "sent" || diffMs <= 0) return fmtDate(iso);
  const hrs = Math.round(diffMs / 36e5);
  if (hrs >= 24) return `${Math.round(hrs / 24)}d remaining (${fmtDate(iso)})`;
  return `${hrs}h remaining (${fmtDate(iso)})`;
}

function fmtCents(c, currency) {
  if (c == null) return "—";
  return `${currency || "USD"} ${(c / 100).toFixed(2)}`;
}

/**
 * CP-14.6.4 polish — human-readable audit-event summary.
 *
 * Replaces the raw `JSON.stringify(payload_json)` rendering with a
 * one-line, plain-English description per event type. Returns "" when
 * the payload is empty or the event needs no extra context.
 */
function humanizeAuditPayload(eventType, payload) {
  if (!payload) return "";
  const p = payload;
  switch (eventType) {
    case "created":
      return p.on_behalf_of_org_id
        ? `on behalf of org #${p.on_behalf_of_org_id}`
        : "";
    case "sent":
      return p.expires_at ? `expires ${new Date(p.expires_at).toLocaleString()}` : "";
    case "accepted":
      return p.reason ? `reason: ${p.reason}` : "";
    case "declined":
      return p.reason ? `reason: ${p.reason}` : "";
    case "withdrawn":
      return p.reason ? `reason: ${p.reason}` : "";
    case "expired":
      if (p.reason === "sla_expiry") return "72h SLA elapsed";
      if (p.reason === "lazy_expiry_on_respond") return "expired on respond attempt";
      return p.reason ? `reason: ${p.reason}` : "";
    case "messaged":
      return p.body_length
        ? `${p.body_length} char${p.body_length === 1 ? "" : "s"}` +
            (p.on_behalf_of_org_id ? ` · org #${p.on_behalf_of_org_id}` : "")
        : "";
    default:
      return "";
  }
}

// ============================================================================
// Inbox page — /app/rfq
// ============================================================================

export function RFQInbox() {
  return (
    <Layout>
      <RFQInboxInner />
    </Layout>
  );
}

function RFQInboxInner() {
  const [tab, setTab] = useState("sent");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api(`/rfq/${tab}`)
      .then((r) => {
        if (!cancelled) setItems(r.items || []);
      })
      .catch((e) => {
        if (!cancelled) toast.error(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab]);

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid={TID.rfqInboxRoot}>
      <header className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">My RFQs</h1>
        <p className="text-sm text-[var(--avcp-ink-soft)] mt-1">
          Requests for Quote you&apos;ve sent or received. RFQs open a
          communication thread — no money moves until you book a project
          separately.
        </p>
      </header>

      <div className="flex items-center gap-2 mb-4">
        <button
          type="button"
          data-testid={TID.rfqInboxTabSent}
          onClick={() => setTab("sent")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium ${
            tab === "sent"
              ? "bg-[var(--avcp-primary-soft)] text-[var(--avcp-primary)]"
              : "text-[var(--avcp-ink-soft)] hover:text-[var(--avcp-ink)]"
          }`}
        >
          Sent
        </button>
        <button
          type="button"
          data-testid={TID.rfqInboxTabInbox}
          onClick={() => setTab("inbox")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium ${
            tab === "inbox"
              ? "bg-[var(--avcp-primary-soft)] text-[var(--avcp-primary)]"
              : "text-[var(--avcp-ink-soft)] hover:text-[var(--avcp-ink)]"
          }`}
        >
          Inbox
        </button>
      </div>

      <section className="avcp-card overflow-hidden">
        {loading && (
          <div className="p-6 text-sm text-[var(--avcp-ink-soft)]">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div
            className="p-8 text-center text-sm text-[var(--avcp-ink-soft)]"
            data-testid={TID.rfqInboxEmpty}
          >
            {tab === "sent"
              ? "You haven't sent any RFQs yet. Try a Crew Shape Search."
              : "Your inbox is empty."}
          </div>
        )}
        {!loading && items.length > 0 && (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-slate-200">
                <th className="p-3">#</th>
                <th className="p-3">{tab === "sent" ? "Recipient" : "Sender"}</th>
                <th className="p-3">Marketplace</th>
                <th className="p-3">Status</th>
                <th className="p-3">Hourly total</th>
                <th className="p-3">Expiry</th>
                <th className="p-3">Created</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                  data-testid={`${TID.rfqInboxRow}-${r.id}`}
                >
                  <td className="p-3 tabular-nums">{r.id}</td>
                  <td className="p-3">
                    {tab === "sent" ? r.recipient_display : r.sender_display}
                  </td>
                  <td className="p-3">{r.marketplace_code}</td>
                  <td className="p-3">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="p-3 tabular-nums">
                    {fmtCents(
                      r.totals?.estimated_hourly_total_cents,
                      r.totals?.currency
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    {expiryLabel(r.expires_at, r.status)}
                  </td>
                  <td className="p-3 text-xs">{fmtDate(r.created_at)}</td>
                  <td className="p-3">
                    <Link
                      to={`/app/rfq/${r.id}`}
                      className="avcp-btn ghost text-xs"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

// ============================================================================
// Detail page — /app/rfq/:id
// ============================================================================

export function RFQDetail() {
  return (
    <Layout>
      <RFQDetailInner />
    </Layout>
  );
}

function RFQDetailInner() {
  const { id } = useParams();
  const rfqId = Number(id);
  const { user } = useAuth();
  const nav = useNavigate();

  const [rfq, setRfq] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadOpen, setThreadOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  const refresh = async () => {
    try {
      const [r, m, e] = await Promise.all([
        api(`/rfq/${rfqId}`),
        api(`/rfq/${rfqId}/messages`),
        api(`/rfq/${rfqId}/events`),
      ]);
      setRfq(r);
      setMessages(m.items || []);
      setThreadOpen(!!m.thread_open);
      setEvents(e.items || []);
    } catch (err) {
      toast.error(err.message || "Could not load RFQ.");
      if (err.status === 403 || err.status === 404) {
        nav("/app/rfq");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [rfqId]);

  if (loading || !rfq) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-sm text-[var(--avcp-ink-soft)]">
        Loading RFQ…
      </div>
    );
  }

  const snap = rfq.snapshot_json || {};
  const senderUserId = rfq.sender_user_id;
  const senderOrgId = rfq.sender_org_id;
  const recipientUserId = rfq.recipient_user_id;
  const recipientOrgId = rfq.recipient_org_id;

  // Server-computed capability flags (CP-14.6.3). The frontend used to
  // guess these from user.id vs sender/recipient ids, but the auth/me
  // response exposes `uuid`, not `id`, and org membership is server-only.
  // The backend now returns an authoritative `viewer` block.
  const viewer = rfq.viewer || {};
  const showAccept = !!viewer.can_accept;
  const showDecline = !!viewer.can_decline;
  const showWithdraw = !!viewer.can_withdraw;
  const status = rfq.status;

  const doRespond = async (decision) => {
    setBusy(true);
    try {
      await api(`/rfq/${rfqId}/respond`, {
        method: "POST",
        body: { decision },
      });
      toast.success(`RFQ ${decision}ed.`);
      await refresh();
    } catch (e) {
      toast.error(e.message || `Failed to ${decision}.`);
    } finally {
      setBusy(false);
    }
  };

  const doWithdraw = async () => {
    if (!window.confirm("Withdraw this RFQ? The thread will become read-only.")) {
      return;
    }
    setBusy(true);
    try {
      await api(`/rfq/${rfqId}/withdraw`, { method: "POST", body: {} });
      toast.success("RFQ withdrawn.");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Failed to withdraw.");
    } finally {
      setBusy(false);
    }
  };

  const sendMessage = async () => {
    const body_text = draft.trim();
    if (!body_text) return;
    setBusy(true);
    try {
      await api(`/rfq/${rfqId}/messages`, {
        method: "POST",
        body: { body_text },
      });
      setDraft("");
      await refresh();
    } catch (e) {
      toast.error(e.message || "Failed to send message.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid={TID.rfqDetailRoot}>
      <header className="flex items-start justify-between gap-4">
        <div>
          <Link to="/app/rfq" className="text-xs text-[var(--avcp-ink-soft)] hover:underline">
            ← Back to My RFQs
          </Link>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">
            RFQ #{rfq.id}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <StatusBadge status={status} testid={TID.rfqDetailStatus} />
            <span
              className="text-xs text-[var(--avcp-ink-soft)]"
              data-testid={TID.rfqDetailExpiry}
            >
              {expiryLabel(rfq.expires_at, status)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showAccept && (
            <button
              type="button"
              className="avcp-btn primary"
              data-testid={TID.rfqDetailAcceptBtn}
              disabled={busy}
              onClick={() => doRespond("accept")}
            >
              Accept
            </button>
          )}
          {showDecline && (
            <button
              type="button"
              className="avcp-btn ghost"
              data-testid={TID.rfqDetailDeclineBtn}
              disabled={busy}
              onClick={() => doRespond("decline")}
            >
              Decline
            </button>
          )}
          {showWithdraw && (
            <button
              type="button"
              className="avcp-btn ghost"
              data-testid={TID.rfqDetailWithdrawBtn}
              disabled={busy}
              onClick={doWithdraw}
            >
              Withdraw
            </button>
          )}
        </div>
      </header>

      <section className="grid md:grid-cols-2 gap-4">
        <div className="avcp-card p-5" data-testid={TID.rfqDetailSender}>
          <div className="text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)]">
            Sender
          </div>
          <div className="text-base font-medium mt-1">
            {snap?.sender?.display_name || `user:${senderUserId}`}
          </div>
          {senderOrgId && (
            <div className="text-xs text-[var(--avcp-ink-soft)]">
              acting on behalf of org #{senderOrgId}
            </div>
          )}
        </div>
        <div className="avcp-card p-5" data-testid={TID.rfqDetailRecipient}>
          <div className="text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)]">
            Recipient
          </div>
          <div className="text-base font-medium mt-1">
            {snap?.recipient?.display_name ||
              (recipientOrgId ? `org:${recipientOrgId}` : `user:${recipientUserId}`)}
          </div>
          <div className="text-xs text-[var(--avcp-ink-soft)]">
            {snap?.recipient?.kind} · marketplace: {rfq.marketplace_code}
          </div>
        </div>
      </section>

      <section className="avcp-card p-5" data-testid={TID.rfqDetailSnapshot}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Crew shape (snapshot)</h2>
          <span className="text-xs text-[var(--avcp-ink-soft)]">
            Immutable — captured at send time
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-slate-200">
                <th className="p-2">Resource</th>
                <th className="p-2 w-20">Qty</th>
                <th className="p-2">Hourly rate</th>
              </tr>
            </thead>
            <tbody>
              {(snap.crew_requirements || []).map((r, i) => (
                <tr
                  key={i}
                  className="border-b border-slate-100"
                  data-testid={`${TID.rfqDetailSnapshotRow}-${i}`}
                >
                  <td className="p-2">
                    <div className="font-medium">{r.title_label_at_send}</div>
                    <div className="text-xs text-[var(--avcp-ink-soft)]">
                      {r.title_code}
                    </div>
                  </td>
                  <td className="p-2 tabular-nums">{r.quantity}</td>
                  <td className="p-2 tabular-nums">
                    {fmtCents(r.estimated_hourly_rate_cents, snap.totals?.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-300 font-semibold">
                <td className="p-2 text-right" colSpan={2}>
                  Hourly total
                </td>
                <td className="p-2 tabular-nums">
                  {fmtCents(
                    snap.totals?.estimated_hourly_total_cents,
                    snap.totals?.currency
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        {snap.notes && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] mb-1">
              Notes
            </div>
            <p className="text-sm whitespace-pre-wrap">{snap.notes}</p>
          </div>
        )}
        {(snap.track_slug || snap.region_code) && (
          <div className="mt-3 text-xs text-[var(--avcp-ink-soft)] flex gap-4">
            {snap.track_slug && <span>Track: {snap.track_slug}</span>}
            {snap.region_code && <span>Region: {snap.region_code}</span>}
          </div>
        )}
      </section>

      <section className="avcp-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Messages</h2>
          {!threadOpen && (
            <span className="text-xs text-[var(--avcp-ink-soft)]">
              Thread closed (status: {status})
            </span>
          )}
        </div>
        <div className="space-y-3 max-h-[400px] overflow-y-auto" data-testid={TID.rfqDetailThread}>
          {messages.length === 0 && (
            <p className="text-sm text-[var(--avcp-ink-soft)]">
              No messages yet. Start the conversation below.
            </p>
          )}
          {messages.map((m) => {
            const mine = m.author_user_id === viewer.user_id;
            return (
              <div
                key={m.id}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
                data-testid={`${TID.rfqDetailThreadMessage}-${m.id}`}
              >
                <div
                  className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                    mine
                      ? "bg-[var(--avcp-primary-soft)] text-[var(--avcp-ink)]"
                      : "bg-slate-100 text-slate-800"
                  }`}
                >
                  <div className="whitespace-pre-wrap">{m.body_text}</div>
                  <div className="text-[10px] text-[var(--avcp-ink-soft)] mt-1">
                    {fmtDate(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {threadOpen ? (
          <div className="mt-4 border-t border-slate-100 pt-4" data-testid={TID.rfqDetailComposer}>
            <textarea
              className="avcp-input w-full min-h-[80px]"
              placeholder="Type your message (text only, up to 4000 chars)…"
              data-testid={TID.rfqDetailComposerInput}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={4000}
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-[var(--avcp-ink-soft)]">
                {draft.length} / 4000 · text only, no attachments
              </span>
              <button
                type="button"
                className="avcp-btn primary"
                data-testid={TID.rfqDetailComposerSend}
                disabled={busy || !draft.trim()}
                onClick={sendMessage}
              >
                Send message
              </button>
            </div>
          </div>
        ) : (
          <div
            className="mt-4 border-t border-slate-100 pt-4 text-sm text-[var(--avcp-ink-soft)]"
            data-testid={TID.rfqDetailComposerClosed}
          >
            This thread is read-only because the RFQ is {status}.
          </div>
        )}
      </section>

      <section>
        <button
          type="button"
          className="text-xs text-[var(--avcp-ink-soft)] hover:text-[var(--avcp-ink)] underline"
          data-testid={TID.rfqDetailAuditToggle}
          onClick={() => setAuditOpen((v) => !v)}
        >
          {auditOpen ? "Hide" : "Show"} audit timeline ({events.length})
        </button>
        {auditOpen && (
          <div className="avcp-card p-4 mt-2" data-testid={TID.rfqDetailAuditDrawer}>
            <ol className="space-y-2">
              {events.map((e) => {
                const human = humanizeAuditPayload(e.event_type, e.payload_json);
                return (
                  <li
                    key={e.id}
                    className="flex items-start gap-3 text-sm"
                    data-testid={`${TID.rfqDetailAuditRow}-${e.id}`}
                  >
                    <span className="text-xs uppercase tracking-wide bg-slate-100 text-slate-700 rounded px-2 py-0.5">
                      {e.event_type}
                    </span>
                    <span className="text-xs text-[var(--avcp-ink-soft)]">
                      {fmtDate(e.created_at)}
                      {e.actor_kind === "system" ? " · system" : ""}
                    </span>
                    {human && (
                      <span className="text-xs text-[var(--avcp-ink-soft)]">
                        · {human}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </section>
    </div>
  );
}
